/**
 * Form Validation Hook
 *
 * Reusable hook for form validation following SoC principles.
 * This hook handles:
 * - Validation state management
 * - Field touched tracking
 * - Error computation
 *
 * UI components use this hook but don't contain validation logic.
 */

import { useState, useCallback, useMemo } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface FieldConfig {
  /** Field key in the config object */
  field: string;
  /** Human-readable label for error messages */
  label: string;
  /** Whether this field is required */
  required?: boolean;
  /** Condition function - field is only validated if this returns true */
  condition?: () => boolean;
  /** Custom validation function */
  validate?: (value: string) => string | null;
}

export interface ValidationState {
  isValid: boolean;
  errors: Record<string, string>;
  missingFields: string[];
}

export interface UseFormValidationOptions<T> {
  /** The form configuration object */
  config: T;
  /** Field configurations defining validation rules */
  fields: FieldConfig[];
  /** Function to extract value from config field */
  getValue?: (config: T, field: string) => string;
}

export interface UseFormValidationResult {
  /** Current validation state */
  validation: ValidationState;
  /** Set of touched field names */
  touchedFields: Set<string>;
  /** Whether to show all validation errors */
  showValidationErrors: boolean;
  /** Mark a field as touched */
  markFieldTouched: (field: string) => void;
  /** Show all validation errors */
  setShowValidationErrors: (show: boolean) => void;
  /** Get error for a specific field (respects touched state) */
  getFieldError: (field: string) => string | undefined;
  /** Check if form can proceed (all validations pass) */
  canProceed: (additionalConditions?: boolean) => boolean;
  /** Reset validation state */
  resetValidation: () => void;
}

// ============================================================================
// Default Value Extractor
// ============================================================================

/**
 * Default function to extract value from config
 * Assumes config fields have a .value property
 */
function defaultGetValue<T>(config: T, field: string): string {
  const fieldValue = (config as Record<string, unknown>)[field];
  if (fieldValue && typeof fieldValue === 'object' && 'value' in fieldValue) {
    return (fieldValue as { value: string }).value || '';
  }
  return '';
}

// ============================================================================
// Validation Logic (Pure Functions)
// ============================================================================

/**
 * Compute validation state from config and field definitions
 * This is a pure function - no side effects
 */
export function computeValidation<T>(
  config: T,
  fields: FieldConfig[],
  getValue: (config: T, field: string) => string = defaultGetValue
): ValidationState {
  const errors: Record<string, string> = {};
  const missingFields: string[] = [];

  for (const fieldConfig of fields) {
    // Skip if condition is defined and returns false
    if (fieldConfig.condition && !fieldConfig.condition()) {
      continue;
    }

    const value = getValue(config, fieldConfig.field);

    // Check required
    if (fieldConfig.required) {
      if (!value || value.trim() === '') {
        errors[fieldConfig.field] = `${fieldConfig.label} is required`;
        missingFields.push(fieldConfig.label);
        continue;
      }
    }

    // Run custom validation if provided
    if (fieldConfig.validate && value) {
      const customError = fieldConfig.validate(value);
      if (customError) {
        errors[fieldConfig.field] = customError;
        missingFields.push(fieldConfig.label);
      }
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    missingFields,
  };
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Form validation hook
 *
 * @example
 * ```tsx
 * const { validation, getFieldError, markFieldTouched } = useFormValidation({
 *   config: formConfig,
 *   fields: [
 *     { field: 'name', label: 'Name', required: true },
 *     { field: 'email', label: 'Email', required: true, validate: validateEmail },
 *   ],
 * });
 * ```
 */
export function useFormValidation<T>({
  config,
  fields,
  getValue = defaultGetValue,
}: UseFormValidationOptions<T>): UseFormValidationResult {
  // Local state for UI concerns
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // Compute validation (memoized)
  const validation = useMemo(
    () => computeValidation(config, fields, getValue),
    [config, fields, getValue]
  );

  // Mark field as touched
  const markFieldTouched = useCallback((field: string) => {
    setTouchedFields((prev) => new Set(prev).add(field));
  }, []);

  // Get error for a field (respects touched state)
  const getFieldError = useCallback(
    (field: string): string | undefined => {
      if (showValidationErrors || touchedFields.has(field)) {
        return validation.errors[field];
      }
      return undefined;
    },
    [showValidationErrors, touchedFields, validation.errors]
  );

  // Check if form can proceed
  const canProceed = useCallback(
    (additionalConditions: boolean = true): boolean => {
      return validation.isValid && additionalConditions;
    },
    [validation.isValid]
  );

  // Reset validation state
  const resetValidation = useCallback(() => {
    setTouchedFields(new Set());
    setShowValidationErrors(false);
  }, []);

  return {
    validation,
    touchedFields,
    showValidationErrors,
    markFieldTouched,
    setShowValidationErrors,
    getFieldError,
    canProceed,
    resetValidation,
  };
}

export default useFormValidation;
